import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl tracking-tight text-[#fafafa]">{title}</h2>
    <div className="mt-3 space-y-3 text-base font-light leading-relaxed text-[#888]">
      {children}
    </div>
  </section>
);

const LegalLayout = ({ title, testId, children }) => {
  useEffect(() => {
    document.title = `${title} — Grandlinecards`;
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return () => {
      document.title =
        "Grandlinecards | Carte Pokémon e One Piece TCG — Catalogo e Prezzi";
    };
  }, [title]);

  return (
    <div data-testid={testId} className="min-h-screen px-6 py-28 md:px-12">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="mb-12 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-[#cba153]"
        >
          <ArrowLeft size={14} />
          Torna al catalogo
        </Link>
        <div className="flex items-center gap-4">
          <img
            src="/assets/grandline-logo.jpg"
            alt="Grandlinecards"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-[#cba153]/60"
          />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#cba153]">
            Grandlinecards
          </p>
        </div>
        <h1 className="mt-6 font-display text-4xl tracking-tighter text-[#fafafa] md:text-5xl">
          {title}
        </h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Ultimo aggiornamento: Agosto 2026
        </p>
        {children}
      </div>
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" testId="privacy-policy-page">
    <Section title="1. Titolare del trattamento">
      <p>Il titolare del trattamento dei dati è Grandlinecards.</p>
      <p>
        Sede legale: Viale Libia 58, 00199 Roma (RM)
        <br />
        P.IVA: IT18599661008
        <br />
        REA: 1798990
        <br />
        Email: grandlinecards149@gmail.com
        <br />
        PEC: alessiorita@legalmail.it
        <br />
        WhatsApp: 339 276 4821
      </p>
    </Section>
    <Section title="2. Dati raccolti">
      <p>
        Questo sito è una vetrina: non richiede registrazione, non raccoglie dati
        personali tramite moduli e non utilizza cookie di profilazione.
      </p>
      <p>
        Se ci contatti tramite WhatsApp, WhatsApp LLC tratterà il tuo numero e i
        messaggi secondo la propria informativa privacy. Noi utilizzeremo il tuo
        numero solo per rispondere alla tua richiesta e gestire l'ordine.
      </p>
    </Section>
    <Section title="3. Finalità e base giuridica">
      <p>
        I dati forniti volontariamente via WhatsApp sono trattati per rispondere
        alle richieste, confermare disponibilità e prezzi e concludere la vendita
        (esecuzione di misure precontrattuali e contrattuali, art. 6 GDPR).
      </p>
    </Section>
    <Section title="4. Conservazione">
      <p>
        Le conversazioni sono conservate per il tempo necessario a gestire
        l'ordine e gli obblighi fiscali, poi cancellate su richiesta.
      </p>
    </Section>
    <Section title="5. I tuoi diritti">
      <p>
        Puoi chiedere in qualsiasi momento accesso, rettifica, cancellazione o
        limitazione del trattamento dei tuoi dati scrivendoci su WhatsApp. Hai
        inoltre diritto di reclamo al Garante per la protezione dei dati
        personali.
      </p>
    </Section>
  </LegalLayout>
);

export const CookiePolicy = () => (
  <LegalLayout title="Cookie Policy" testId="cookie-policy-page">
    <Section title="1. Cosa sono i cookie">
      <p>
        I cookie sono piccoli file di testo che i siti salvano sul tuo
        dispositivo per funzionare correttamente o raccogliere statistiche.
      </p>
    </Section>
    <Section title="2. Cookie utilizzati da questo sito">
      <p>
        Questo sito utilizza esclusivamente cookie e strumenti tecnici,
        necessari al funzionamento delle pagine e a ricordare la tua scelta sul
        banner dei cookie. Non vengono installati cookie di profilazione,
        pubblicitari o di terze parti.
      </p>
    </Section>
    <Section title="3. Cookie di terze parti">
      <p>
        Il sito non integra servizi di analisi, pixel pubblicitari o pulsanti
        social che installino cookie. I link verso WhatsApp, Instagram e TikTok
        aprono siti esterni che applicano le proprie informative.
      </p>
    </Section>
    <Section title="4. Come disattivare i cookie">
      <p>
        Puoi bloccare o cancellare i cookie dalle impostazioni del tuo browser
        (Chrome, Safari, Firefox, Edge). La disattivazione dei cookie tecnici
        può compromettere alcune funzioni del sito.
      </p>
    </Section>
    <Section title="5. Contatti">
      <p>
        Per domande su questa Cookie Policy: Grandlinecards, Viale Libia 58,
        00199 Roma (RM) — email grandlinecards149@gmail.com — PEC
        alessiorita@legalmail.it.
      </p>
    </Section>
  </LegalLayout>
);

export const TerminiCondizioni = () => (
  <LegalLayout title="Termini e Condizioni" testId="termini-page">
    <Section title="1. Oggetto">
      <p>
        Questo sito è il catalogo vetrina di Grandlinecards: mostra prodotti e
        prezzi di carte collezionabili Pokémon e One Piece. Non è un
        e-commerce con pagamento online: gli ordini si concludono tramite
        WhatsApp.
      </p>
    </Section>
    <Section title="2. Ordini e prezzi">
      <p>
        I prezzi indicati sono in euro e possono variare senza preavviso. Il
        prezzo finale e la disponibilità sono sempre confermati in chat prima
        del pagamento. L'ordine si considera confermato solo dopo il nostro
        riscontro scritto su WhatsApp.
      </p>
    </Section>
    <Section title="3. Disponibilità">
      <p>
        Lo stato "Disponibile" o eventuali tempi di attesa (es. "Disponibile tra
        2 settimane") sono indicativi e aggiornati periodicamente. In caso di
        articolo esaurito dopo la conferma, proponiamo rimborso o alternativa.
      </p>
    </Section>
    <Section title="4. Pagamento e consegna">
      <p>
        Metodi di pagamento, spedizione o ritiro sono concordati in chat.
        Eventuali spese di spedizione sono comunicate prima della conferma
        dell'ordine.
      </p>
    </Section>
    <Section title="5. Diritto di recesso e resi">
      <p>
        Per gli acquisti a distanza valgono i diritti previsti dal Codice del
        Consumo, incluso il recesso entro 14 giorni per i prodotti sigillati e
        integri. I prodotti aperti o con sigillo rimosso non possono essere resi.
      </p>
    </Section>
    <Section title="6. Marchi">
      <p>
        Pokémon, One Piece e tutti i marchi e personaggi citati appartengono ai
        rispettivi titolari. Grandlinecards vende prodotti originali e non è
        affiliata a The Pokémon Company, Nintendo o Bandai Namco.
      </p>
    </Section>
    <Section title="7. Contatti">
      <p>Per qualsiasi domanda su questi termini puoi contattarci:</p>
      <p>
        Grandlinecards — Sede legale: Viale Libia 58, 00199 Roma (RM)
        <br />
        P.IVA: IT18599661008
        <br />
        REA: 1798990
        <br />
        Email: grandlinecards149@gmail.com
        <br />
        PEC: alessiorita@legalmail.it
        <br />
        WhatsApp: 339 276 4821
      </p>
    </Section>
  </LegalLayout>
);

export default LegalLayout;
